import React from 'react';
import { useDispatch } from 'react-redux';
import styled from '@emotion/styled';
import { ZIndex } from '@styles/z-index';
import { ThemeProps } from '@theme/base';
import { AppVariant } from '@model/config/brands';
import { PromoBannerData } from '@model/promo-banner';
import { TripTypes } from '@model/common';
import { disallowedPages } from '@model/common/pages/promo-banner-pages';
import Cookies, { Cookie } from '@model/common/cookie/cookie';
import { getShowModalPayload, ModalType, showModal } from '@state/slices/modal/modalSlice';
import { useResize } from '@components/hooks';
import { bannerIsActive } from '@util/promo';
import { CountdownBanner } from '../countdown/CountdownBanner';

export interface PromoBannerProps {
  currentPath: string;
  promoBanners: Array<PromoBannerData>;
  tripType: string | null;
  epc?: string;
}

const PromoBanner = styled(CountdownBanner)(({ theme }: ThemeProps) => ({
  padding: theme.custom.spacing.small,
  zIndex: ZIndex.CONTENT
}));

export const PromoBannerComponent = ({ currentPath, promoBanners, tripType, epc }: PromoBannerProps) => {
  const dispatch = useDispatch();
  const [_, isMobile] = useResize();
  const isWhiteLabel = AppVariant.isWhitelabel();
  if (isWhiteLabel) {
    return null;
  }

  if (!promoBanners?.length) {
    return null;
  }

  const getBasePath = (currentPath: string): string => {
    const [basePath] = currentPath.split('?');
    return basePath;
  };

  const pagesIncludesPath = (pages: string[], currentPath: string): boolean => {
    return pages.includes(getBasePath(currentPath));
  };

  const pagesMatchesPath = (pages: string[], currentPath: string): boolean => {
    const basePath = getBasePath(currentPath);
    return pages.some((path) => basePath === path || basePath.startsWith(`${path}/`) || basePath.endsWith(`${path}/`));
  };

  const pagesHasPathSegment = (pages: string[], currentPath: string): boolean => {
    const pathSegments = getBasePath(currentPath)
      .split('/')
      .filter((segment) => segment.length > 0);

    return pages.some((path) => {
      return pathSegments.some((segment) => path.includes(`/${segment}`) || path.includes(segment));
    });
  };

  const displayBanner = (banner: PromoBannerData, currentPath: string, tripTypeProps: string): boolean => {
    const tripTypes: Array<TripTypes> = banner.tripType ?? [TripTypes.ALL];

    return tripTypes.some((tripType: TripTypes) => {
      if (
        pagesIncludesPath(disallowedPages, currentPath) ||
        pagesMatchesPath(disallowedPages, currentPath) ||
        pagesHasPathSegment(disallowedPages, currentPath)
      ) {
        return false;
      }
      return tripType === tripTypeProps;
    });
  };

  const getActiveBanners = (currentPath: string, tripType: string): Array<PromoBannerData> => {
    return promoBanners.filter((banner: PromoBannerData) => {
      return bannerIsActive(banner) && displayBanner(banner, currentPath, tripType);
    });
  };

  if (!tripType) {
    return null;
  }

  const activeBanners: Array<PromoBannerData> = getActiveBanners(currentPath, tripType);

  if (activeBanners.length === 0) {
    return null;
  }

  const epcBanner: PromoBannerData | undefined = epc
    ? activeBanners.find(({ epc: bannerEpc }) => bannerEpc === epc)
    : undefined;
  const firstBanner: PromoBannerData | undefined = epcBanner || activeBanners.find(({ epc }) => !epc);

  if (!firstBanner) {
    return null;
  }

  const { backgroundColour, textColour, title, text, mobileText, activeTo, epc: bannerEpc } = firstBanner;
  const handleOnClick = () => {
    if (bannerEpc) {
      new Cookies().destroy(Cookie.CLOSE_EPC);
      dispatch(showModal(getShowModalPayload(ModalType.EPC_MODAL)));
    } else {
      dispatch(showModal(getShowModalPayload(ModalType.PROMO_BANNER_MODAL, firstBanner.modal)));
    }
  };
  const bannerText = isMobile ? mobileText : text;

  return (
    <PromoBanner
      bannerButtonText={isMobile ? '' : title}
      activeTo={activeTo}
      bannerText={bannerText}
      bannerBackgroundColor={backgroundColour}
      bannerTextColor={textColour}
      onClick={handleOnClick}
    />
  );
};
